import React, { useState, useRef, useEffect } from 'react';
import { Info, HelpCircle } from 'lucide-react';
import './HelpTooltip.css';

interface HelpTooltipProps {
  text: string;
  title?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  icon?: 'info' | 'help';
  size?: number;
  maxWidth?: number;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({
  text,
  title,
  position = 'top',
  icon = 'info',
  size = 14,
  maxWidth = 300,
}) => {
  const [visible, setVisible] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (visible && tooltipRef.current && triggerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();

      // Auto-adjust if tooltip would go off-screen
      if (position === 'top' && triggerRect.top - tooltipRect.height < 10) {
        setAdjustedPosition('bottom');
      } else if (position === 'bottom' && triggerRect.bottom + tooltipRect.height > window.innerHeight - 10) {
        setAdjustedPosition('top');
      } else if (position === 'left' && triggerRect.left - tooltipRect.width < 10) {
        setAdjustedPosition('right');
      } else if (position === 'right' && triggerRect.right + tooltipRect.width > window.innerWidth - 10) {
        setAdjustedPosition('left');
      } else {
        setAdjustedPosition(position);
      }
    }
  }, [visible, position]);

  const IconComponent = icon === 'help' ? HelpCircle : Info;

  return (
    <span
      className="help-tooltip-wrapper"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      ref={triggerRef}
      tabIndex={0}
      role="button"
      aria-label={title || 'Help information'}
    >
      <IconComponent size={size} className="help-tooltip-icon" />
      {visible && (
        <div
          ref={tooltipRef}
          className={`help-tooltip-content help-tooltip-${adjustedPosition}`}
          style={{ maxWidth: `${maxWidth}px` }}
          role="tooltip"
        >
          {title && <div className="help-tooltip-title">{title}</div>}
          <div className="help-tooltip-text">{text}</div>
        </div>
      )}
    </span>
  );
};

export default HelpTooltip;
